/**
 * `cloudgrid feedback` — explicit user-volunteered feedback. Submits an
 * `event_type: 'feedback'` event through the same telemetry pipeline as
 * auto error/success events (decision 016).
 *
 * Three modes:
 *   - `cloudgrid feedback "<text>"` — one-line message.
 *   - `cloudgrid feedback "<text>" --include-log` — appends scrubbed last
 *     200 lines of `~/.cloudgrid/logs/cli.log`. Useful when a user is
 *     reporting a recent flake.
 *   - `cloudgrid feedback --interactive` — opens `$EDITOR` with a template.
 *     Default fallback when no `<text>` arg is given.
 *
 * Replaces the v0.7 legacy-client `feedback` verb (backlog 048; deleted
 * with the rest of the legacy client). The new shape posts straight to
 * `POST /api/v2/telemetry/cli-events`.
 */

import { Command } from 'commander';
import { existsSync, readFileSync, writeFileSync, mkdtempSync, rmSync } from 'fs';
import { spawnSync } from 'child_process';
import { join } from 'path';
import { tmpdir } from 'os';
import { CloudGridError, log } from '../utils.js';
import { readCredentials } from '../credentials.js';
import { notSignedInMessage } from '../active-org.js';
import { recordCliEvent, scrubLocal } from '../services/telemetry.js';
import { getCloudGridHome } from '../home.js';

interface FeedbackOpts {
  includeLog?: boolean;
  interactive?: boolean;
}

const LOG_TAIL_LINES = 200;
const LOG_EXCERPT_MAX_BYTES = 32 * 1024;

const INTERACTIVE_TEMPLATE = [
  '# Write your feedback below. Lines starting with `#` are ignored.',
  '# What were you trying to do? What did you expect? What happened instead?',
  '',
  '',
].join('\n');

export async function runFeedback(
  message: string | undefined,
  opts: FeedbackOpts,
): Promise<void> {
  // Decision 016 §"Privacy and anonymization": the auto-event opt-out
  // gates silent paths only. Typing `cloudgrid feedback` is itself the
  // consent, so the verb submits regardless of `isTelemetryEnabled()`.
  const creds = readCredentials();
  if (!creds || !creds.jwt) {
    throw new CloudGridError(notSignedInMessage());
  }

  // Resolve the message body — explicit arg, interactive editor, or error.
  // The "no positional + no --interactive" case defaults to interactive
  // per decision 016 §"CLI integration" (the default is the friendly path).
  let body = (message ?? '').trim();
  if (!body || opts.interactive) {
    body = await openEditorForMessage();
  }
  if (!body) {
    throw new CloudGridError('Feedback message is empty.');
  }

  let logExcerpt: string | null = null;
  let lineCount = 0;
  if (opts.includeLog) {
    const result = readScrubbedLogTail();
    logExcerpt = result.text;
    lineCount = result.lineCount;
  }

  await recordCliEvent(
    {
      event_type: 'feedback',
      command: 'cloudgrid feedback',
      text: scrubLocal(body),
      log_excerpt: logExcerpt,
    },
    { force: true },
  );

  // §23 voice: plain, specific, "Thank you" is acknowledgment, no marketing.
  if (logExcerpt !== null) {
    log.success(`✓ Sent to CloudGrid (with log excerpt — ${lineCount} lines). Thank you.`);
  } else {
    log.success('✓ Sent to CloudGrid. Thank you.');
  }
}

/** Read tail of `~/.cloudgrid/logs/cli.log` with credential scrubbing. */
function readScrubbedLogTail(): { text: string; lineCount: number } {
  const logPath = join(getCloudGridHome(), 'logs', 'cli.log');
  if (!existsSync(logPath)) {
    return { text: '(no log file)', lineCount: 0 };
  }
  const raw = readFileSync(logPath, 'utf-8');
  const lines = raw.split('\n');
  // `tail -n 200` shape; honour an empty trailing newline by trimming.
  const tail = lines.slice(Math.max(0, lines.length - LOG_TAIL_LINES));
  const scrubbed = scrubLocal(tail.join('\n'));
  // Cap at 32KB after scrub so a runaway log doesn't bloat the event.
  const capped = Buffer.byteLength(scrubbed, 'utf-8') > LOG_EXCERPT_MAX_BYTES
    ? truncateUtf8(scrubbed, LOG_EXCERPT_MAX_BYTES)
    : scrubbed;
  return { text: capped, lineCount: tail.length };
}

/** Truncate a string to fit under `maxBytes` UTF-8 bytes. */
function truncateUtf8(s: string, maxBytes: number): string {
  const buf = Buffer.from(s, 'utf-8');
  if (buf.length <= maxBytes) return s;
  // Slice and decode; truncate may land mid-codepoint, which decoder
  // tolerates with `�`. Acceptable for logs — the alternative is a
  // codepoint walk and the readability gain is marginal.
  return buf.subarray(buf.length - maxBytes).toString('utf-8');
}

/**
 * Open `$EDITOR` (default `vi`) on a temp file pre-seeded with a comment
 * template. Returns the user's body with `#`-prefixed lines stripped and
 * surrounding whitespace trimmed.
 */
async function openEditorForMessage(): Promise<string> {
  const editor = process.env.EDITOR || process.env.VISUAL || 'vi';
  const dir = mkdtempSync(join(tmpdir(), 'cloudgrid-feedback-'));
  const path = join(dir, 'FEEDBACK.txt');
  try {
    writeFileSync(path, INTERACTIVE_TEMPLATE);
    const res = spawnSync(editor, [path], { stdio: 'inherit' });
    if (res.error) {
      throw new CloudGridError(`Failed to open editor (${editor}): ${res.error.message}`);
    }
    if (res.status !== 0) {
      throw new CloudGridError(`Editor (${editor}) exited with status ${res.status}.`);
    }
    const raw = readFileSync(path, 'utf-8');
    return raw
      .split('\n')
      .filter((line) => !line.trimStart().startsWith('#'))
      .join('\n')
      .trim();
  } finally {
    rmSync(dir, { recursive: true, force: true });
  }
}

export const feedbackCommand = new Command('feedback')
  .description('Send feedback to the CloudGrid team (decision 016)')
  .argument('[message]', 'One-line feedback message; opens $EDITOR if omitted')
  .option('--include-log', `Attach the scrubbed last ${LOG_TAIL_LINES} lines of ~/.cloudgrid/logs/cli.log`)
  .option('--interactive', 'Open $EDITOR even when a message argument is given')
  .action(async (message: string | undefined, opts: FeedbackOpts) => {
    await runFeedback(message, opts);
  });

// `cloudgrid feedback list` — alias for `cloudgrid telemetry list --type
// feedback` (backlog 095). Lazy-imported so feedback.ts doesn't drag the
// telemetry handler into the bundle when only `cloudgrid feedback "..."`
// is used.
feedbackCommand
  .command('list')
  .description('List feedback events (alias of `cloudgrid telemetry list --type feedback`)')
  .option('--user-email <email>', 'Filter by user email (admin only)')
  .option('--org <slug>', 'Filter by grid slug')
  .option('--since <duration>', 'Restrict to events newer than this (e.g. 24h, 7d)')
  .option('--limit <n>', 'Number of events to return (default 50, max 200)')
  .option('--json', 'Emit the API response shape directly')
  .action(async (opts: { userEmail?: string; org?: string; since?: string; limit?: string; json?: boolean }) => {
    const { runTelemetryList } = await import('./telemetry.js');
    const { handleV2Error } = await import('../handle-error.js');
    try {
      await runTelemetryList({ ...opts, type: 'feedback' });
    } catch (err) {
      await handleV2Error(err, { json: Boolean(opts.json) });
    }
  });
